import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import ShareOutlined from "@mui/icons-material/ShareOutlined";

export default function ClipResultCard({ clip, onCopyCaption, onShare, lang }) {
  const theme = useTheme();

  if (clip.error) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "error.light" }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} color="error.main">
            {clip.title || "Clip error"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {clip.errorMessage || lang?.clippingClipError || "This clip failed to render."}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, height: "100%" }}>
        <Box
          sx={{
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: alpha(theme.palette.common.black, 0.06),
            aspectRatio: "9 / 16",
            maxHeight: 280,
          }}
        >
          {clip.recompiling ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }} spacing={1}>
              <CircularProgress size={28} />
              <Typography variant="caption" color="text.secondary">
                {lang?.clippingRecompiling || "Recompiling…"}
              </Typography>
            </Stack>
          ) : clip.video_url ? (
            <Box
              component="video"
              src={clip.video_url}
              controls
              playsInline
              preload="metadata"
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
              <Chip
                label={`🔥 ${clip.viral_score ?? "—"}`}
                color="primary"
                size="small"
                sx={{ fontWeight: 800 }}
              />
            </Stack>
          )}
        </Box>

        <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.35 }}>
          {clip.title}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" label={`⏱ ${Math.round(clip.duration || 0)}s`} variant="outlined" />
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`🔥 ${lang?.clippingViralScore || "Viral"}: ${clip.viral_score ?? "—"}`}
          />
        </Stack>

        {clip.reason && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {clip.reason}
          </Typography>
        )}

        {clip.description && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, flex: 1 }}>
            {clip.description}
          </Typography>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: "auto", pt: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyOutlined />}
            onClick={() => onCopyCaption?.(clip.description)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {lang?.clippingCopyCaption || "Copy caption"}
          </Button>
          {onShare && clip.video_url && (
            <Button
              size="small"
              variant="contained"
              startIcon={<ShareOutlined />}
              onClick={() => onShare(clip)}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              {lang?.aiClippingShare || "Share"}
            </Button>
          )}
          {clip.video_url && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadOutlined />}
              onClick={() => window.open(clip.video_url, "_blank", "noopener,noreferrer")}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              {lang?.clippingDownload || "Download MP4"}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
